import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { Icd10Code, Icd10Entry, Icd10Name } from '@dreryk/egabinet-icd-dictionary-published-language-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';
import { Disease, DiseaseQuery } from '@dreryk/medical-dictionaries-api';

import { CqrsQueryPerformResult, performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import { FieldControllerProps, TranslatableText } from '@dreryk/react-common';
import { Autosuggest, fonts } from '@dreryk/react-components';
import { TextPattern } from '@dreryk/watermelon';

import { commonTranslationKeys } from '../../../translations';

type DiagnosisSelectorProps = FieldControllerProps<'diagnosis', Icd10Entry | null> & {
   readonly visitDiagnoses: VisitDiagnosis[] | undefined;
   readonly required?: boolean;
   readonly label?: TranslatableText;
};

const DiagnosisSelectorDropdownPrimaryText = styled.div`
   font-weight: ${fonts.fontWeightBold};
   display: inline;
`;
DiagnosisSelectorDropdownPrimaryText.displayName = 'DiagnosisSelectorDropdownPrimaryText';

const diseaseLabelExtractor = (disease: Icd10Entry): string => {
   return disease.getName().asString();
};

const diseaseItemRenderer = (item: Icd10Entry): JSX.Element => {
   return (
      <div>
         <DiagnosisSelectorDropdownPrimaryText>{item.getName().asString()}</DiagnosisSelectorDropdownPrimaryText>
         {` (${item.getCode().asString()})`}
      </div>
   );
};

const useSearchDiseasesCreator = (searchText: string | null): (() => CqrsQueryPerformResult<Icd10Entry[]>) => {
   return useCallback(
      async () =>
         performCqrsQuerySearch<Disease>(() => {
            const query = new DiseaseQuery();
            if (searchText) {
               query.nameOrIcd10Code = TextPattern.anywhereIgnoreCase(searchText);
            }
            return query;
         }).then((diseases) =>
            diseases.map(({ icd10Code, name }) => Icd10Entry.of(Icd10Code.of(icd10Code), Icd10Name.of(name)))
         ),
      [searchText]
   );
};

export const DiagnosisSelector = (props: DiagnosisSelectorProps): JSX.Element => {
   const defaultItems = useMemo<Icd10Entry[] | undefined>(() => {
      return props.visitDiagnoses?.map((diagnosis) =>
         Icd10Entry.of(Icd10Code.of(diagnosis.icd10Code), Icd10Name.of(diagnosis.name))
      );
   }, [props.visitDiagnoses]);

   return (
      <Autosuggest<Icd10Entry>
         minChars={2}
         keyProperty={(icd10Entry: Icd10Entry) => icd10Entry.getCode().asString()}
         placeholder={commonTranslationKeys.fillICD10OrDiseaseName}
         defaultItems={defaultItems}
         itemRenderer={diseaseItemRenderer}
         labelExtractor={diseaseLabelExtractor}
         controller={props.register(props.name, { required: props.required })}
         searchCreator={useSearchDiseasesCreator}
         testId='disease'
         label={props.label}
      />
   );
};
